/**
 * U12.1 / U12.2 / U12.3 / U12.4 — Format helpers (Sprint B/C WO hardening).
 *
 * - `formatDateTime(iso)` — ISO → `dd.MM.yyyy HH:mm` (или `—` для null). Устойчив к невалидному ISO.
 * - `formatDecimal(val, opts?)` — trim trailing zeros до maxDecimals (default 4).
 * - `formatQty(val, uom?)` — `"{trimmed} {uom_localised}"` либо `"{trimmed}"`.
 * - `formatMoney({amount, currency})` — `"{grouped} {currency}"` с separator (ru-RU).
 * - `formatEntityRef({id, name?, sku?, code?})` — `name (sku/code)` либо `id.slice(0,8)`.
 *
 * UoM локализация:
 *   БД содержит смешанные коды (`meter`, `Piece`, `kg`, `кг`, `шт`, `литр`...).
 *   `normaliseUomCode` приводит к canonical i18n-ключу из `@staiter/i18n` (`m`, `pcs`, `kg`, `L`...).
 *   При missing key — fallback на raw uom (без toast, без crash).
 */

import * as m from '@staiter/i18n';
import { getLanguageTag } from '@staiter/i18n';
import {
  displaySign,
  formatDate as sharedFormatDate,
  formatDateTime as sharedFormatDateTime,
  formatNumber as sharedFormatNumber,
  normaliseUomCode as sharedNormaliseUomCode,
} from '@staiter/i18n/format';
import type { Locale } from '@staiter/shared';
import { LOCALE_BCP47 } from '@staiter/shared';

/**
 * FE-76 follow-up: shared helper для boolean → «Да / Нет / —» с distinguishing null vs false.
 * Никогда не возвращает em-dash для confirmed bool — это критично для accounting accuracy.
 */
export function formatBoolean(value: boolean | null | undefined): string {
  if (value === true) return m.common_yes();
  if (value === false) return m.common_no();
  return '—';
}

/**
 * D4.5 (TE-F3, решение владельца 02.08) — дата и время идут через общий форматтер
 * `@staiter/i18n/format`, а не собираются руками.
 *
 * Прежняя реализация склеивала `dd.MM.yyyy HH:mm` из `getDate()/getMonth()`:
 * формат не зависел от локали вообще, и в en-локали дата со временем оставались
 * русскими независимо от переводов — системное отступление от правила 19.
 * Исключение к правилу решением владельца НЕ заводится.
 *
 * Правка сделана здесь, а не в 18 местах употребления: источник один, и ни одно
 * место нельзя пропустить по невнимательности. Сигнатура сохранена, поэтому
 * вызывающие не трогаются — меняется только то, что они получают на выходе.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return sharedFormatDateTime(iso, getLanguageTag() as Locale);
}

/**
 * F15 — «сегодня» в ЛОКАЛЬНОЙ зоне пользователя, а не в UTC.
 *
 * `new Date().toISOString().slice(0,10)` в Казахстане (UTC+5) до 05:00 отдаёт
 * вчерашнюю дату: диалог «создать проект» открывался с датой, которая для
 * пользователя уже прошла, и план молча уезжал на день назад. Собираем строку
 * из локальных компонент — никакой конверсии зон, потому что «сегодня» и есть
 * локальное понятие.
 */
export function localTodayIso(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** D4.5 — то же для даты без времени: формат следует локали пользователя. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return sharedFormatDate(iso, getLanguageTag() as Locale);
}

/**
 * D4.3 (TE-F6) — числовой формат по локали пользователя.
 *
 * Заводится ЗДЕСЬ, а не по местам: девятнадцать вызовов `toLocaleString('ru-KZ')`
 * и `toLocaleDateString('ru-RU')` были рассыпаны по экранам, и каждый из них в
 * en-локали продолжал печатать русские разделители независимо от переводов.
 * Класс `locale` барьера BB-2 — самый вредный именно этим: переведённый экран
 * остаётся наполовину русским в числах и датах.
 */
export function formatNumber(
  value: string | number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  return sharedFormatNumber(value, getLanguageTag() as Locale, options);
}

/**
 * Trim trailing zeros; keep at most `maxDecimals` (default 4). Null-safe.
 *
 * Гейт C пака stock-entry-wizard, раунд 3 — РАЗРЯДЫ.
 *
 * Возвращалось `String(rounded)`: «11657», «284.2185» — без разделителя тысяч
 * и с точкой вместо запятой, тогда как деньги в соседней колонке той же
 * таблицы печатались «1 022 105,47 ₸». Дословно ревьюера: «оператор сверяет их
 * глазами и переводит в уме». Класс, а не место: на этом форматтере стоят все
 * количества продукта, включая `formatQty`.
 *
 * Разделитель нормализуется тем же способом, что в `formatMoney`: Chromium
 * ставит в `ru-RU` узкий неразрывный пробел, в Inter почти неотличимый от
 * отсутствия пробела.
 */
export function formatDecimal(
  val: string | number | null | undefined,
  opts?: { maxDecimals?: number; minDecimals?: number },
): string {
  if (val == null || val === '') return '—';
  const s = typeof val === 'number' ? val.toString() : val;
  const n = Number(s);
  if (Number.isNaN(n)) return s;
  const max = opts?.maxDecimals ?? 4;
  // F101 (С-I, NC-45): знак минус U+2212 в показе — тем же помощником, что в `@staiter/i18n/format`.
  return displaySign(
    normalizeSeparators(
      new Intl.NumberFormat(LOCALE_BCP47[getLanguageTag() as Locale], {
        maximumFractionDigits: max,
        // `minDecimals` нужен денежным колонкам: без него «5 312,5» и
        // «132 812,50» встают в столбце с разной длиной дробной части, и глаз
        // перестаёт сравнивать разряды. У количеств хвостовые нули, наоборот,
        // шум — потому по умолчанию их нет.
        ...(opts?.minDecimals !== undefined ? { minimumFractionDigits: opts.minDecimals } : {}),
        useGrouping: true,
      }).format(n),
    ),
  );
}

/** Re-export нормализатора UoM, чтобы не дублировать карту. */
export const normaliseUomCode = sharedNormaliseUomCode;

/**
 * Локализует UoM через `@staiter/i18n`. При missing key — возвращает raw (dev-warning в consoln).
 */
export function formatUom(uom: string | null | undefined): string {
  if (!uom) return '';
  const canonical = normaliseUomCode(uom);
  const localised = m.units_short({ code: canonical });
  if (localised.startsWith('[missing:')) return uom;
  return localised;
}

/** `"{trimmed-decimal} {uom_localised}"` — используется в Overview/Materials. */
export function formatQty(val: string | number | null | undefined, uom?: string | null): string {
  const d = formatDecimal(val);
  if (d === '—') return '—';
  if (!uom) return d;
  const localised = formatUom(uom);
  return localised ? `${d} ${localised}` : d;
}

export type Money = { amount: string; currency: string };

/**
 * Chromium 116+ изменил `ru-RU` group-separator на NNBSP (U+202F). В Inter
 * NNBSP едва различимо — числа выглядят слитно (`123586817.11`).
 *
 * Нормализуем все non-breaking space варианты к ASCII space. RegExp строим
 * программно через charCode — Biome auto-formatter коллапсит литералы
 * NBSP/NNBSP/thin-space в regex (видит как duplicate spaces) и ломает паттерн.
 */
const NBSP_RE = new RegExp(`[${String.fromCharCode(0x00a0, 0x2009, 0x202f)}]`, 'g');

function normalizeSeparators(s: string): string {
  return s.replace(NBSP_RE, ' ');
}

/**
 * Format money с разделителями тысяч + всегда 2 fraction digits.
 *   `123586817.11` → `123 586 817,11 KZT` (ru-RU)
 *
 * Decimal-safe для display: amount приходит как string. Trailing zeros
 * сохраняем (`100.00` не `100`), чтобы columns в финансовой таблице aligned.
 */
// FE-85/91/94: currency code → glyph (₸/$/€/₽/¥) consistency с money.ts:formatMoney.
const CURRENCY_GLYPH: Record<string, string> = {
  KZT: '₸',
  USD: '$',
  EUR: '€',
  RUB: '₽',
  CNY: '¥',
};

export function formatMoney(money: Money | null | undefined): string {
  if (!money) return '—';
  const cur = (money.currency ?? 'KZT').toUpperCase();
  const n = Number(money.amount);
  if (!Number.isFinite(n)) return `${money.amount} ${CURRENCY_GLYPH[cur] ?? cur}`;
  const formatted = normalizeSeparators(
    // D4.3 — было жёстко 'ru-RU': КАЖДАЯ денежная величина приложения печаталась
    // русскими разделителями в любой локали. Форма подачи (число + глиф валюты)
    // сохранена намеренно — меняется только язык разбивки.
    new Intl.NumberFormat(LOCALE_BCP47[getLanguageTag() as Locale], {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(n),
  );
  return `${displaySign(formatted)} ${CURRENCY_GLYPH[cur] ?? cur}`;
}

export function formatEntityRef(entity: {
  id: string;
  name?: string | null;
  sku?: string | null;
  code?: string | null;
}): string {
  const label = entity.name;
  const secondary = entity.sku ?? entity.code;
  if (label && secondary) return `${label} (${secondary})`;
  if (label) return label;
  if (secondary) return secondary;
  return entity.id.slice(0, 8);
}
